import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import type {
  WizardDefinition,
  WizardEndDetails,
  WizardEndReason,
} from './types';
import {
  findInitialWizardStepIndex,
  findWizardStepIndexWithTarget,
} from './targetUtils';
import { useWizardsAllowed } from './wizardViewport';

export type WizardContextValue = {
  activeWizard: WizardDefinition | null;
  stepIndex: number;
  startWizard: (definition: WizardDefinition) => void;
  endWizard: (reason: WizardEndReason) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
};

const WizardContext = createContext<WizardContextValue | null>(null);

export function WizardProvider({
  children,
  onWizardEnd,
}: {
  children: ReactNode;
  onWizardEnd?: (
    wizardId: string,
    reason: WizardEndReason,
    details?: WizardEndDetails,
  ) => void;
}) {
  const [activeWizard, setActiveWizard] = useState<WizardDefinition | null>(
    null,
  );
  const [stepIndex, setStepIndex] = useState(0);
  const activeWizardIdRef = useRef<string | null>(null);
  const activeWizardRef = useRef(activeWizard);
  /** Highest 0-based step index reached this tour (survives Back navigation). */
  const maxStepIndexSeenRef = useRef(0);
  const wizardsAllowed = useWizardsAllowed();

  activeWizardRef.current = activeWizard;

  const goToStepIndex = useCallback((index: number) => {
    maxStepIndexSeenRef.current = Math.max(maxStepIndexSeenRef.current, index);
    setStepIndex(index);
  }, []);

  const endWizard = useCallback(
    (reason: WizardEndReason) => {
      const wizardId = activeWizardIdRef.current;
      const details: WizardEndDetails | undefined = wizardId
        ? {
            lastStepSeen: maxStepIndexSeenRef.current + 1,
            totalSteps: activeWizardRef.current?.steps.length ?? 0,
          }
        : undefined;
      setActiveWizard(null);
      setStepIndex(0);
      activeWizardIdRef.current = null;
      maxStepIndexSeenRef.current = 0;
      if (wizardId) {
        onWizardEnd?.(wizardId, reason, details);
      }
    },
    [onWizardEnd],
  );

  const startWizard = useCallback(
    (definition: WizardDefinition) => {
      if (!wizardsAllowed) {
        return;
      }

      const initialIndex = findInitialWizardStepIndex(definition);
      if (initialIndex === null) {
        onWizardEnd?.(definition.id, 'skipped');
        return;
      }

      activeWizardIdRef.current = definition.id;
      maxStepIndexSeenRef.current = initialIndex;
      setActiveWizard(definition);
      setStepIndex(initialIndex);
    },
    [onWizardEnd, wizardsAllowed],
  );

  useEffect(() => {
    if (!wizardsAllowed && activeWizard) {
      endWizard('skipped');
    }
  }, [activeWizard, endWizard, wizardsAllowed]);

  const goToNextStep = useCallback(() => {
    if (!activeWizard) {
      return;
    }

    const nextIndex = findWizardStepIndexWithTarget(
      activeWizard,
      stepIndex + 1,
      1,
    );
    if (nextIndex === null) {
      endWizard('completed');
      return;
    }
    goToStepIndex(nextIndex);
  }, [activeWizard, endWizard, goToStepIndex, stepIndex]);

  const goToPreviousStep = useCallback(() => {
    if (!activeWizard) {
      return;
    }

    const previousIndex = findWizardStepIndexWithTarget(
      activeWizard,
      stepIndex - 1,
      -1,
    );
    if (previousIndex === null) {
      return;
    }
    goToStepIndex(previousIndex);
  }, [activeWizard, goToStepIndex, stepIndex]);

  const value = useMemo(
    (): WizardContextValue => ({
      activeWizard,
      stepIndex,
      startWizard,
      endWizard,
      goToNextStep,
      goToPreviousStep,
    }),
    [
      activeWizard,
      endWizard,
      goToNextStep,
      goToPreviousStep,
      startWizard,
      stepIndex,
    ],
  );

  return (
    <WizardContext.Provider value={value}>{children}</WizardContext.Provider>
  );
}

export function useWizard() {
  const context = useContext(WizardContext);
  if (!context) {
    throw new Error('useWizard must be used within WizardProvider');
  }
  return context;
}

export function useWizardOptional() {
  return useContext(WizardContext);
}
